import { useDispatchWrapper } from "../../../hooks";
import { useAuth } from "../../../contexts/AuthProvider";
import { upsertUserSettingAsync } from "../../../services/siteConfiguration.service";
import { showMessage } from "../../../redux/messages/messagesActions";
import { WHATSAPP_SERVICE_CONFIG } from "../../../constants/whatsapp.constatnts";
import produce from "immer";

interface IUseWhatsAppServiceToggleOptions {
  onSuccess?: () => void;
  onSwitch?: () => void;
}

export const useWhatsAppServiceToggle = (
  options: IUseWhatsAppServiceToggleOptions = {},
) => {
  const { onSuccess, onSwitch } = options;
  const dispatch = useDispatchWrapper();
  const { userSettings, getActiveAuth, updateUserData } = useAuth();

  const handleChange = async (toBailey: boolean) => {
    const newService = toBailey ? "BAILEY" : "META";
    const service = userSettings.find(
      (st) =>
        st.env_key === WHATSAPP_SERVICE_CONFIG.CURRENT_SELECTED_WHATSAPP_PLATFORM,
    );

    if (!service) return;

    const currentAuth = getActiveAuth();

    try {
      // Optimistically update auth context so the toggle reflects immediately
      const updatedSettings = produce(currentAuth, (draft) => {
        draft.userSettings = draft.userSettings.map((sett) => {
          if (
            sett.env_key ===
            WHATSAPP_SERVICE_CONFIG.CURRENT_SELECTED_WHATSAPP_PLATFORM
          ) {
            return { ...sett, setting_value: newService };
          }
          return sett;
        });
      });

      updateUserData(updatedSettings);
      onSwitch?.();

      await upsertUserSettingAsync({
        user_settings_uuid: service.user_settings_uuid,
        master_company_outlet_env_uuid: service.master_company_outlet_env_uuid,
        user_uuid: service.user_uuid,
        setting_value: newService,
        status: "ACTIVE",
      });

      onSuccess?.();

      dispatch(
        showMessage({
          type: "success",
          message: `Switched to ${newService === "BAILEY" ? "WhatsApp QR" : "Meta WhatsApp"} WhatsApp`,
          displayAs: "snackbar",
        }),
      );
    } catch (error) {
      // Revert optimistic update on failure
      const revertedService = toBailey ? "META" : "BAILEY";
      updateUserData(
        produce(currentAuth, (draft) => {
          draft.userSettings = draft.userSettings.map((sett) => {
            if (
              sett.env_key ===
              WHATSAPP_SERVICE_CONFIG.CURRENT_SELECTED_WHATSAPP_PLATFORM
            ) {
              return { ...sett, setting_value: revertedService };
            }
            return sett;
          });
        }),
      );

      console.error("Failed to update WhatsApp service:", error);
      dispatch(
        showMessage({
          type: "error",
          message: "Failed to update WhatsApp service. Please try again.",
          displayAs: "snackbar",
        }),
      );
    }
  };

  return { handleChange };
};
